import { useCallback, useEffect, useRef, useState } from "react";
import type {
  Ad,
  ClaimOfferErrorCode,
  OfferClaimStatus,
  OfferClaimedAssets,
} from "@b2bportal/media-ads-api";
import {
  ClaimOfferResponseEnum,
  ClickActionEnum,
  EventType,
} from "@b2bportal/media-ads-api";
import { claimMediaOffer, trackAdEvents } from "../api/mediaAds";
import { useHtsMediaRequestMetadata } from "../providers/HtsMediaRequestMetadataProvider";

export interface ClaimResult {
  /** Assets from the successfully claimed offer */
  assets: OfferClaimedAssets;
  /** URI to navigate to after claiming (if the ad specifies UriNavigation) */
  navigateUri: string | undefined;
  /** Claim outcome from the backend (Success or UserPreviouslyClaimed). */
  offerClaimStatus?: OfferClaimStatus;
}

/** Structured claim error with the backend error code when available. */
export interface ClaimOfferError {
  errorCode?: ClaimOfferErrorCode;
  message: string;
}

export interface UseClaimOfferOptions {
  /** Callback fired after a successful claim */
  onSuccess?: (result: ClaimResult) => void;
  /** Callback fired when claiming fails */
  onError?: (error: ClaimOfferError) => void;
}

/**
 * Provides a reusable claim flow for HTS Media ad offers.
 *
 * Handles the full lifecycle:
 * 1. Tracks a click event (fire-and-forget)
 * 2. Calls the claim endpoint
 * 3. Interprets the response (success/failure)
 * 4. Manages loading and error state
 */
export const useClaimOffer = (options?: UseClaimOfferOptions) => {
  const requestMetadata = useHtsMediaRequestMetadata();
  const [claiming, setClaiming] = useState(false);
  const [claimResult, setClaimResult] = useState<ClaimResult | undefined>();
  const [claimError, setClaimError] = useState<ClaimOfferError | undefined>();

  // Ref-based guard prevents duplicate claims from rapid successive clicks.
  // State-based `claiming` alone is insufficient because setState is async —
  // two clicks can both read `claiming === false` before either sets it true.
  const claimingRef = useRef(false);

  // Keep latest callback refs to avoid stale closures in useCallback
  const onSuccessRef = useRef(options?.onSuccess);
  const onErrorRef = useRef(options?.onError);
  useEffect(() => {
    onSuccessRef.current = options?.onSuccess;
    onErrorRef.current = options?.onError;
  });

  const claimOffer = useCallback(
    async (ad: Ad) => {
      if (ad.offerTemplate == null || claimingRef.current) return;

      claimingRef.current = true;
      setClaiming(true);
      setClaimError(undefined);

      try {
        // Step 1: Track click event (fire and forget)
        trackAdEvents({
          events: [
            {
              eventType: EventType.Click,
              trackingData: { token: ad.trackingData },
              occurredAt: new Date().toISOString(),
            },
          ],
        }).catch((e) => console.error("Failed to track click event", e));

        // Step 2: Claim the offer
        const response = await claimMediaOffer({
          offerTemplateId: ad.offerTemplate.offerTemplateId,
          requestMetadata,
        });

        const result = response.data;

        if (result.ClaimOfferResponse === ClaimOfferResponseEnum.Success) {
          const claimResult: ClaimResult = {
            assets: result.offer.offerClaimedAssets,
            navigateUri:
              ad.clickAction.ClickAction === ClickActionEnum.UriNavigation
                ? ad.clickAction.uri
                : undefined,
            offerClaimStatus: result.offerClaimStatus,
          };
          setClaimResult(claimResult);
          onSuccessRef.current?.(claimResult);
        } else {
          const claimOfferError: ClaimOfferError = {
            errorCode: result.errorCode,
            message: `Claim failed: ${result.ClaimOfferResponse}`,
          };
          setClaimError(claimOfferError);
          onErrorRef.current?.(claimOfferError);
        }
      } catch (error) {
        const claimOfferError: ClaimOfferError = {
          message:
            error instanceof Error ? error.message : "Failed to claim offer",
        };
        setClaimError(claimOfferError);
        onErrorRef.current?.(claimOfferError);
        console.error("Failed to claim offer", error);
      } finally {
        claimingRef.current = false;
        setClaiming(false);
      }
    },
    [requestMetadata]
  );

  const resetClaim = useCallback(() => {
    setClaimResult(undefined);
    setClaimError(undefined);
  }, []);

  return {
    claimOffer,
    claiming,
    claimResult,
    claimError,
    resetClaim,
  };
};
